import React, { ReactNode } from 'react';
import { SnackbarKey, SnackbarProvider } from 'notistack';
import { useSnackBarProviderIntl } from './translations/useSnackBarProviderIntl';
// biome-ignore lint/style/noRestrictedImports: we need makeStyles to customize notistack
import { makeStyles } from '@material-ui/core';
import { tokens, Button, Icon, styled } from '@beeline.com/bp-design-system';

// NOTE: Feature flags will not work in this component.
const SnackbarCustomProvider: React.FC<React.PropsWithChildren<{ children: ReactNode | ReactNode[] }>> = ({
  children
}) => {
  const intl = useSnackBarProviderIntl();
  const classes = useStyles();
  const notistackRef = React.useRef<SnackbarProvider | null>(null);
  const onClickDismiss = (snackbarId: SnackbarKey | undefined) => () => {
    notistackRef?.current?.closeSnackbar(snackbarId);
  };

  return (
    <SnackbarProvider
      // After migration to MUI v5 this line can be removed. It is required for MUI v4
      // https://github.com/iamhosseindhv/notistack/issues/394#issuecomment-877688487
      style={{ pointerEvents: 'all', margin: '0.5rem' }}
      // Render snackbars into document.body rather than inside #root. FullScreenFormView hides #root via
      // `display: none` for the native elastic-overscroll effect, which would otherwise hide any snackbar
      // rendered within #root (e.g. form submit errors).
      domRoot={document.body}
      maxSnack={5}
      classes={{
        variantInfo: classes.variantInfo,
        variantSuccess: classes.variantSuccess,
        root: classes.root
      }}
      data-test="notistack-snackbar"
      ref={notistackRef}
      iconVariant={{
        info: <StyledInfoIcon name="info" size={18} />
      }}
      action={(key) => (
        <Button variant="text" onClick={onClickDismiss(key)} dataTest="snackbar__notification__button">
          {intl.dismiss}
        </Button>
      )}
    >
      {children}
    </SnackbarProvider>
  );
};

export default SnackbarCustomProvider;

const useStyles = makeStyles(() => ({
  variantInfo: {
    backgroundColor: tokens.colorPrimaryContainer,
    color: tokens.colorOnPrimaryContainer,
    '& > div > button': {
      color: tokens.colorOnPrimaryContainer
    }
  },
  variantSuccess: {
    backgroundColor: tokens.colorSuccessContainer,
    color: tokens.colorOnSuccessContainer,
    '& > div > button': {
      color: tokens.colorOnSuccessContainer
    }
  },
  root: {
    '& > div': {
      boxShadow: tokens.elevationLevel3
    },
    '& button': {
      color: tokens.colorOnPrimary
    }
  }
}));

const StyledInfoIcon = styled(Icon)`
  color: ${({ theme }) => theme.tokens.colorPrimary};
  margin-right: ${({ theme }) => theme.spacingSize.m};
`;
